import { useEffect, useState } from 'react';
import { NavLink, Navigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Landmark } from 'lucide-react';
import {
  fromMinor,
  schemas,
  toMinor,
  type BankAccount,
  type NetBankingAccess,
  type PaymentCard,
} from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Modal, PageHeader, Money } from '@/components/ui';
import { describeError } from '@/lib/errors';
import { humanize } from '@/lib/format';
import { CrudPage, RecordForm, type FieldDefinition } from './CrudPage';
import { BankAccountsPage } from './index';
import { LoansSection } from './loans/LoansSection';

// Cards and net banking logins live inside the bank account they draw on;
// their old addresses (…/cards, …/net-banking) land on Bank Accounts.
const SECTIONS = [
  {
    key: 'bank-accounts',
    label: 'Bank Accounts',
    short: 'Bank',
    icon: Building2,
    permission: 'payment_account:read',
  },
  { key: 'loans', label: 'Loans', short: 'Loans', icon: Landmark, permission: 'loan:read' },
] as const;

/**
 * Every way the company pays, on one page: its bank accounts — each with the
 * debit and credit cards and net banking logins that draw on it — and the
 * loans it repays from them, picked from the panel's side list.
 */
export function PaymentAccountsPage() {
  const { section } = useParams<{ section: string }>();
  const { can } = useAuth();
  const sections = SECTIONS.filter((entry) => can(entry.permission));
  if (!sections.some((entry) => entry.key === section)) {
    return <Navigate to={`/settings/payment-accounts/${sections[0]?.key ?? ''}`} replace />;
  }

  return (
    <>
      <PageHeader
        title="Payment Accounts"
        subtitle="Bank accounts, cards and net banking the company pays through, and the loans it repays"
      />
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <nav
          aria-label="Payment account types"
          className={`grid shrink-0 ${sections.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-1 md:flex md:w-52 md:flex-col`}
        >
          {sections.map(({ key, label, short, icon: Icon }) => (
            <NavLink
              key={key}
              to={`/settings/payment-accounts/${key}`}
              aria-label={label}
              className={({ isActive }) =>
                `flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-2 py-2 text-sm md:justify-start md:px-3 font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-800'
                    : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900'
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              <span className="md:hidden">{short}</span>
              <span className="hidden md:inline">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="min-w-0 flex-1">
          {section === 'loans' ? <LoansSection /> : <BankAccountsSection />}
        </div>
      </div>
    </>
  );
}

const PERMISSIONS = {
  read: 'payment_account:read',
  create: 'bank_account:create',
  update: 'bank_account:update',
  delete: 'bank_account:delete',
} as const;

/** The company's own bank accounts, for a card or login to name the one it draws on. */
function useBankAccountOptions() {
  const { companyId } = useAuth();
  const { data } = useQuery({
    queryKey: ['settings-bank-accounts', companyId, 'options'],
    queryFn: () => api.settings.bankAccounts({ companyId, pageSize: 200 }),
  });
  const accounts = (data?.items ?? []).filter((account) => account.active);
  return {
    options: accounts.map((account) => ({
      value: account.id,
      label: `${account.label} (…${account.accountNumber.slice(-4)})`,
    })),
    labelOf: (id?: string) => accounts.find((account) => account.id === id)?.label,
  };
}

/** Limits are typed as whole amounts and stored in minor units. */
function withMinor(body: Record<string, unknown>, field: string) {
  return typeof body[field] === 'number' ? { ...body, [field]: toMinor(body[field]) } : body;
}

/** Every card and login the company has, to count and place under each account. */
function usePaymentMethods() {
  const { companyId } = useAuth();
  const cards = useQuery({
    queryKey: [`${METHODS}-all-cards`, companyId],
    queryFn: () => api.settings.paymentCards({ companyId, pageSize: 200 }),
  });
  const logins = useQuery({
    queryKey: [`${METHODS}-all-logins`, companyId],
    queryFn: () => api.settings.netBanking({ companyId, pageSize: 200 }),
  });
  const of = <T extends { bankAccountId?: string; active: boolean }>(
    items: T[] | undefined,
    account: string | null,
  ) => (items ?? []).filter((item) => (item.bankAccountId || null) === account);
  return {
    cardsOf: (account: string | null) => of(cards.data?.items, account),
    loginsOf: (account: string | null) => of(logins.data?.items, account),
  };
}

/** Query keys of everything on this page that lists cards or logins. */
const METHODS = 'payment-methods';

/** Re-reads every list of cards and logins, e.g. once an account's form closes. */
function useRefreshPaymentMethods() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0]).startsWith(METHODS),
    });
}

/**
 * Bank Accounts, each carrying the cards and net banking logins that draw on
 * it: counted on its row, listed and added under its edit form, and shown
 * read-only to whoever may not edit accounts. Anything linked to no account
 * is listed below, so nothing goes missing.
 */
function BankAccountsSection() {
  const { can } = useAuth();
  const methods = usePaymentMethods();
  const refreshMethods = useRefreshPaymentMethods();
  const [viewing, setViewing] = useState<BankAccount | null>(null);
  const [drafts, setDrafts] = useState<MethodDraft[]>([]);
  const [notAdded, setNotAdded] = useState<string[]>([]);
  const canEdit = can(PERMISSIONS.update);
  const unlinked = methods.cardsOf(null).length + methods.loginsOf(null).length;

  // The account is saved by now, so a card or login that fails must not hold its
  // form open: another Save would add the account twice.
  const saveDrafts = async (account: BankAccount) => {
    const results = await Promise.allSettled(
      drafts.map(({ kind, values }) => {
        const body = { ...values, bankAccountId: account.id };
        return kind === 'card'
          ? api.settings.createPaymentCard(withMinor(body, 'creditLimit'))
          : api.settings.createNetBanking(withMinor(body, 'dailyLimit'));
      }),
    );
    setNotAdded(
      results.flatMap((result, index) =>
        result.status === 'rejected'
          ? [`${draftLabel(drafts[index]!)}: ${describeError(result.reason)}`]
          : [],
      ),
    );
    await refreshMethods();
  };

  return (
    <div className="space-y-6">
      <BankAccountsPage
        extraColumns={[
          {
            header: 'Cards & logins',
            render: (row) => {
              const cards = methods.cardsOf(row.id).length;
              const logins = methods.loginsOf(row.id).length;
              return cards || logins ? (
                <span className="text-sm text-ink-700">
                  {cards} card{cards === 1 ? '' : 's'} · {logins} login{logins === 1 ? '' : 's'}
                </span>
              ) : (
                <span className="text-sm text-ink-400">—</span>
              );
            },
          },
        ]}
        rowActions={
          canEdit
            ? undefined
            : (row) => [{ label: 'Cards & logins', run: async () => setViewing(row) }]
        }
        formFooter={(row) => <PaymentMethodsOf account={row} />}
        createFooter={<NewAccountMethods drafts={drafts} onChange={setDrafts} />}
        onCreated={saveDrafts}
      />

      {unlinked ? (
        <section aria-label="Not linked to a bank account" className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-ink-900">Not linked to a bank account</h2>
            <p className="text-sm text-ink-500">
              Cards and logins that name no account. Open one to link it to the account it draws on.
            </p>
          </div>
          {methods.cardsOf(null).length ? <CardsTable account={null} /> : null}
          {methods.loginsOf(null).length ? <LoginsTable account={null} /> : null}
        </section>
      ) : null}

      {viewing ? (
        <Modal title={`${viewing.label} — cards and logins`} onClose={() => setViewing(null)} wide>
          <PaymentMethodsOf account={viewing} />
        </Modal>
      ) : null}

      {notAdded.length ? (
        <Modal title="Not everything was added" onClose={() => setNotAdded([])}>
          <p className="text-sm text-ink-600">
            The bank account was added, but not these. Open the account to add them again.
          </p>
          <ul className="mt-3 space-y-1 text-sm text-ink-900">
            {notAdded.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        </Modal>
      ) : null}
    </div>
  );
}

/** A card or login entered with a new account, saved once the account exists. */
interface MethodDraft {
  kind: 'card' | 'login';
  values: Record<string, unknown>;
}

const draftLabel = ({ kind, values }: MethodDraft) =>
  kind === 'card' ? `${values.label} …${values.last4}` : `${values.label} · ${values.loginId}`;

/** The cards and logins to add with a new account, using the same forms as an existing one. */
function NewAccountMethods({
  drafts,
  onChange,
}: {
  drafts: MethodDraft[];
  onChange(drafts: MethodDraft[]): void;
}) {
  const { companyId } = useAuth();
  const [adding, setAdding] = useState<MethodDraft['kind'] | null>(null);
  // Closing the form, saved or not, leaves nothing for the next account.
  useEffect(() => () => onChange([]), []); // eslint-disable-line react-hooks/exhaustive-deps

  const section = (kind: MethodDraft['kind'], title: string, add: string, subtitle: string) => (
    <section aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-ink-900">{title}</h3>
          <p className="text-sm text-ink-500">{subtitle}</p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => setAdding(kind)}>
          {add}
        </button>
      </div>
      {drafts.some((draft) => draft.kind === kind) ? (
        <ul className="mt-2 divide-y divide-ink-100 rounded-lg border border-ink-100">
          {drafts.map((draft, index) =>
            draft.kind === kind ? (
              <li key={index} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="min-w-0 truncate text-ink-900">{draftLabel(draft)}</span>
                <button
                  type="button"
                  className="text-xs font-medium text-red-700 hover:underline"
                  aria-label={`Remove ${draftLabel(draft)}`}
                  onClick={() => onChange(drafts.filter((other) => other !== draft))}
                >
                  Remove
                </button>
              </li>
            ) : null,
          )}
        </ul>
      ) : null}
    </section>
  );

  const add = async (kind: MethodDraft['kind'], values: Record<string, unknown>) => {
    onChange([...drafts, { kind, values }]);
    setAdding(null);
  };

  return (
    <div className="space-y-5">
      {section(
        'card',
        'Cards',
        'Add card',
        'Optional. Saved with the account. Only the last 4 digits are kept.',
      )}
      {section(
        'login',
        'Net Banking Logins',
        'Add net banking login',
        'Optional. Saved with the account. Only the login ID is kept.',
      )}
      {adding === 'card' ? (
        <RecordForm
          title="Add Card"
          fields={cardFields([]).filter((field) => field.name !== 'bankAccountId')}
          formColumns={2}
          schema={schemas.createPaymentCardRequest}
          initial={{ companyId, ...CARD_DEFAULTS }}
          onClose={() => setAdding(null)}
          onSubmit={(values) => add('card', values)}
        />
      ) : null}
      {adding === 'login' ? (
        <RecordForm
          title="Add Net Banking Login"
          fields={loginFields([]).filter((field) => field.name !== 'bankAccountId')}
          formColumns={2}
          schema={schemas.createNetBankingRequest}
          initial={{ companyId }}
          onClose={() => setAdding(null)}
          onSubmit={(values) => add('login', values)}
        />
      ) : null}
    </div>
  );
}

/** One account's cards and logins, refreshed everywhere once they are closed. */
function PaymentMethodsOf({ account }: { account: BankAccount }) {
  const refresh = useRefreshPaymentMethods();
  // On leaving the account (form or view closed), the counts on its row catch up.
  useEffect(() => () => void refresh(), []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-5" aria-label={`Cards and logins of ${account.label}`}>
      <CardsTable account={account.id} />
      <LoginsTable account={account.id} />
    </div>
  );
}

type Options = NonNullable<FieldDefinition['options']>;

const cardFields = (bankAccountOptions: Options): FieldDefinition[] => [
  { name: 'label', label: 'Label', required: true },
  {
    name: 'cardType',
    label: 'Card type',
    type: 'select',
    required: true,
    options: [
      { value: 'DEBIT', label: 'Debit card' },
      { value: 'CREDIT', label: 'Credit card' },
    ],
  },
  {
    name: 'network',
    label: 'Network',
    type: 'select',
    options: [
      { value: 'VISA', label: 'Visa' },
      { value: 'MASTERCARD', label: 'Mastercard' },
      { value: 'RUPAY', label: 'RuPay' },
      { value: 'AMEX', label: 'American Express' },
      { value: 'DINERS', label: 'Diners Club' },
      { value: 'OTHER', label: 'Other' },
    ],
  },
  { name: 'bankName', label: 'Issuing bank', required: true },
  { name: 'last4', label: 'Last 4 digits', required: true, help: 'Never the full number.' },
  { name: 'expiry', label: 'Expiry (MM/YY)', required: true },
  { name: 'holderName', label: 'Name on card', required: true },
  {
    name: 'bankAccountId',
    label: 'Linked bank account',
    type: 'select',
    options: bankAccountOptions,
  },
  {
    name: 'creditLimit',
    label: 'Credit limit',
    type: 'number',
    visibleWhen: (values) => values.cardType === 'CREDIT',
  },
  { name: 'notes', label: 'Notes' },
];

const CARD_DEFAULTS = { cardType: 'CREDIT', network: 'VISA' };

const loginFields = (bankAccountOptions: Options): FieldDefinition[] => [
  { name: 'label', label: 'Label', required: true },
  { name: 'bankName', label: 'Bank', required: true },
  {
    name: 'loginId',
    label: 'Login / customer ID',
    required: true,
    help: 'Never the password or PIN.',
  },
  {
    name: 'bankAccountId',
    label: 'Bank account',
    type: 'select',
    options: bankAccountOptions,
  },
  { name: 'portalUrl', label: 'Portal address' },
  { name: 'dailyLimit', label: 'Daily transfer limit', type: 'number' },
  { name: 'notes', label: 'Notes' },
];

/** `account` null lists what is linked to no bank account. */
function CardsTable({ account }: { account: string | null }) {
  const bankAccounts = useBankAccountOptions();
  return (
    <CrudPage<PaymentCard>
      embedded
      title="Cards"
      subtitle="Debit and credit cards. Only the last 4 digits are kept, never the full number or CVV."
      queryKey={`${METHODS}-cards-${account ?? 'unlinked'}`}
      permissions={PERMISSIONS}
      list={(query) =>
        scoped(api.settings.paymentCards({ ...query, page: 1, pageSize: 200 }), account)
      }
      create={(body) => api.settings.createPaymentCard(withMinor(body, 'creditLimit'))}
      update={(id, body) => api.settings.updatePaymentCard(id, withMinor(body, 'creditLimit'))}
      remove={(id) => api.settings.deletePaymentCard(id)}
      restore={(id) => api.settings.updatePaymentCard(id, { active: true })}
      schemas={{
        create: schemas.createPaymentCardRequest,
        update: schemas.updatePaymentCardRequest,
      }}
      toFormValues={(row) => ({
        ...row,
        creditLimit: row.creditLimit === undefined ? '' : fromMinor(row.creditLimit),
      })}
      columns={[
        { header: 'Label', render: (row) => <span className="font-medium">{row.label}</span> },
        { header: 'Type', render: (row) => humanize(row.cardType) },
        {
          header: 'Card',
          render: (row) => (
            <span className="font-mono text-xs">
              {humanize(row.network)} …{row.last4}
            </span>
          ),
        },
        { header: 'Bank', render: (row) => row.bankName },
        { header: 'Expires', render: (row) => row.expiry },
        {
          header: 'Credit limit',
          align: 'right',
          render: (row) =>
            row.creditLimit !== undefined && row.creditLimit !== null ? (
              <Money minor={row.creditLimit} currency={row.currency} />
            ) : (
              '—'
            ),
        },
      ]}
      fields={cardFields(bankAccounts.options)}
      defaults={{ ...CARD_DEFAULTS, ...(account ? { bankAccountId: account } : {}) }}
      formColumns={2}
    />
  );
}

/** `account` null lists what is linked to no bank account. */
function LoginsTable({ account }: { account: string | null }) {
  const bankAccounts = useBankAccountOptions();
  return (
    <CrudPage<NetBankingAccess>
      embedded
      title="Net Banking Logins"
      subtitle="Bank portals the company pays through. Only the login ID is kept, never a password or PIN."
      queryKey={`${METHODS}-logins-${account ?? 'unlinked'}`}
      permissions={PERMISSIONS}
      list={(query) =>
        scoped(api.settings.netBanking({ ...query, page: 1, pageSize: 200 }), account)
      }
      create={(body) => api.settings.createNetBanking(withMinor(body, 'dailyLimit'))}
      update={(id, body) => api.settings.updateNetBanking(id, withMinor(body, 'dailyLimit'))}
      remove={(id) => api.settings.deleteNetBanking(id)}
      restore={(id) => api.settings.updateNetBanking(id, { active: true })}
      schemas={{
        create: schemas.createNetBankingRequest,
        update: schemas.updateNetBankingRequest,
      }}
      toFormValues={(row) => ({
        ...row,
        dailyLimit: row.dailyLimit === undefined ? '' : fromMinor(row.dailyLimit),
      })}
      columns={[
        { header: 'Label', render: (row) => <span className="font-medium">{row.label}</span> },
        { header: 'Bank', render: (row) => row.bankName },
        {
          header: 'Login ID',
          render: (row) => <span className="font-mono text-xs">{row.loginId}</span>,
        },
        { header: 'Account', render: (row) => bankAccounts.labelOf(row.bankAccountId) ?? '—' },
        {
          header: 'Daily limit',
          align: 'right',
          render: (row) =>
            row.dailyLimit !== undefined && row.dailyLimit !== null ? (
              <Money minor={row.dailyLimit} currency={row.currency} />
            ) : (
              '—'
            ),
        },
      ]}
      fields={loginFields(bankAccounts.options)}
      defaults={account ? { bankAccountId: account } : undefined}
      formColumns={2}
    />
  );
}

/**
 * Keeps only the rows linked to `account` (or to none). A company has a
 * handful of cards and logins, so all are read and narrowed here.
 */
async function scoped<T extends { bankAccountId?: string }>(
  request: Promise<{ items: T[] }>,
  account: string | null,
) {
  const items = (await request).items.filter((item) => (item.bankAccountId || null) === account);
  return { items, page: 1, pageSize: Math.max(items.length, 1), total: items.length };
}
